// utils/live.js — สายเรียลไทม์ฝั่งเบราว์เซอร์ (WebSocket)
// -----------------------------------------------------------------------------
// ดีไซน์เต็ม: `C:\iStarApp\REALTIME_DESIGN.md` · ฝั่งเซิร์ฟเวอร์อยู่ที่ `istarserver_dev/live.js`
//
// สิ่งที่วิ่งมาตามสายมีแค่ **ชื่อหัวข้อ** (`{topic, path}`) ไม่มีข้อมูลจริงเลย — หน้าจอที่ได้รับ
// สัญญาณไปเรียก API เดิมของตัวเอง ซึ่งผ่าน verifyToken/requireMenu ตามปกติ สิทธิ์ทุกชั้นจึงคงเดิม
//
// หลักการที่ห้ามละเมิด (ทุกข้อมีราคาที่จ่ายมาแล้ว):
//  1. **ห้ามถอดโพลสำรองออกจากหน้าจอ** — สายนี้เป็นของแถม ไม่ใช่ของหลัก · ถ้า DO ขยายเป็นหลาย
//     instance เครื่องที่ถือสายจะไม่ใช่เครื่องที่รับ write เสมอไป หน้าจอต้องยังสดได้ด้วยตัวเอง
//  2. **กลับมาจากพื้นหลัง = ต่อใหม่ + ดึงข้อมูลใหม่เสมอ ห้ามเชื่อ `readyState`** — มือถือที่หลับไป
//     ค้าง `OPEN` ได้เป็นชั่วโมงทั้งที่สายตายไปแล้ว (เจอมาแล้วกับ `spaces_blocked_by_corp_vpn_dns`
//     คนละเรื่องกันแต่อาการเดียวกัน: ตัวแปรบอกว่าปกติ แต่ของจริงไม่มา)
//  3. **ต้องมี jitter ตอนต่อใหม่** — เซิร์ฟเวอร์รีสตาร์ต 01:30 ทุกคืน ถ้าทุกเครื่องต่อกลับ
//     พร้อมกันเป๊ะ ๆ = ยิง `/live-ticket` พร้อมกันทั้งยิมทันทีที่เซิร์ฟเวอร์เพิ่งตื่น
//  4. **`reason: 'disabled'` ห้ามต่อใหม่** — นั่นคือสวิตช์ปิดฉุกเฉินถูกกด ถ้ายังวนต่อสาย
//     จะกลายเป็นยิงรัวใส่เซิร์ฟเวอร์ที่กำลังมีปัญหาอยู่ ซึ่งแย่กว่าไม่มีสายเสียอีก
//  5. **ห้าม throw ออกไปหาหน้าจอ** — จองไม่ได้เพราะสายเรียลไทม์พังคือสิ่งที่รับไม่ได้
// -----------------------------------------------------------------------------

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const JITTER_MS = 5000;          // 0–5 วิ (หลักการข้อ 3)
const MAX_TRIES = 8;             // ต่อไม่ติดติด ๆ กัน = ทางนี้ใช้ไม่ได้จริง เลิกแล้วใช้โพล
const DEBOUNCE_MS = 1200;        // รวบสัญญาณที่มาติด ๆ กันให้เหลือการดึงข้อมูลครั้งเดียว

const subs = new Map();          // topic -> Set<fn>
const sysSubs = new Set();       // ผู้ฟังเรื่อง session (App.vue)

let ws = null;
let getToken = () => null;
let tries = 0;
let timer = null;
let stopped = false;             // สวิตช์ถูกปิด / logout แล้ว — ห้ามต่อใหม่
let starting = false;

function wsUrl() {
  const base = (env && env.SERVER_URL) || '';
  // SERVER_URL อาจเป็น path สัมพัทธ์ (`/istarserver`) เมื่อ deploy อยู่โดเมนเดียวกัน
  const abs = /^https?:\/\//i.test(base) ? base : (window.location.origin + base);
  return abs.replace(/^http/i, 'ws').replace(/\/+$/, '') + '/live';
}

// รวบสัญญาณต่อผู้ฟังหนึ่งตัว — จัดกลุ่มเด็กทั้งห้อง 30 คนยิง 30 สัญญาณติด ๆ กัน ถ้าปล่อยตรง ๆ
// จอที่ฟังอยู่จะดึงข้อมูล 30 รอบ (บางจอยิง 2 endpoint ต่อรอบ) = ภาระ **มากกว่า** ตอนไม่มีระบบนี้
// ซึ่งกลับหัวกับเหตุผลที่สร้างมันขึ้นมา · รวบที่นี่จุดเดียว ไม่ต้องไล่แก้ทุกจอ
const pending = new Map();   // fn -> timeout id

function emit(topic, path) {
  const set = subs.get(topic);
  if (!set) return;
  for (const fn of set) {
    if (pending.has(fn)) clearTimeout(pending.get(fn));
    pending.set(fn, setTimeout(() => {
      pending.delete(fn);
      try { fn({ topic, path }); } catch (e) { /* จอหนึ่งพังต้องไม่ลามไปจออื่น (หลักการข้อ 5) */ }
    }, DEBOUNCE_MS));
  }
}

function emitSystem(msg) {
  for (const fn of sysSubs) {
    try { fn(msg); } catch (e) { /* ห้ามลาม */ }
  }
}

function scheduleReconnect() {
  if (stopped || timer) return;
  // 🔑 **ต้องมีเพดาน** — ถ้าเส้นทาง WS ใช้ไม่ได้จริง (proxy องค์กรบล็อก `wss` · ingress ไม่ส่ง
  // upgrade ต่อให้ backend) การวนต่อใหม่ทุก 30 วิตลอดกาลคือภาระเปล่าที่ไม่มีวันสำเร็จ
  // โพลสำรอง 60 วิ ยังทำงานอยู่ หน้าจอจึงยังสดได้ด้วยตัวเอง (หลักการข้อ 1)
  if (tries >= MAX_TRIES) {
    stopped = true;
    console.log('[live] ต่อสายไม่สำเร็จหลายครั้ง — ใช้โพลสำรองอย่างเดียว');
    return;
  }
  // exponential backoff + jitter — ไม่ใช่ backoff เฉย ๆ (หลักการข้อ 3)
  const wait = Math.min(RECONNECT_BASE_MS * Math.pow(2, tries), RECONNECT_MAX_MS)
    + Math.floor(Math.random() * JITTER_MS);
  tries++;
  timer = setTimeout(() => { timer = null; connect(); }, wait);
}

async function connect() {
  if (stopped || starting) return;
  if (ws && ws.readyState === WebSocket.OPEN) return;
  const token = getToken();
  if (!token) return;                       // ยังไม่ได้ล็อกอิน — ไม่ใช่ความผิดพลาด

  starting = true;
  try {
    // ตั๋วใช้ครั้งเดียว อายุ 30 วิ — เบราว์เซอร์ส่ง Authorization ตอนเปิด WebSocket ไม่ได้
    const res = await fetch(((env && env.SERVER_URL) || '') + '/live-ticket', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: '{}',
    });
    if (res.status === 503) {                // สวิตช์ปิดฉุกเฉิน — ถอยไปโพลอย่างเดียว (ข้อ 4)
      stopped = true;
      emitSystem({ topic: 'live-disabled' });
      return;
    }
    // 404 = เซิร์ฟเวอร์ยังไม่มีช่องทางนี้ (บันเดิลใหม่วิ่งอยู่บน backend รุ่นเก่า — เกิดได้จริง
    // ตอน deploy ไม่พร้อมกัน และ CDN แคช index.html ไว้ถึง 24 ชม.) · **ห้ามวนต่อ** ไม่งั้น
    // ทุกเบราว์เซอร์ที่เปิดอยู่จะยิง /live-ticket ทุก 30 วิตลอดไปโดยไม่มีวันสำเร็จ
    if (res.status === 404) {
      stopped = true;
      console.log('[live] เซิร์ฟเวอร์ยังไม่รองรับเรียลไทม์ — ใช้โพลสำรองอย่างเดียว');
      return;
    }
    if (res.status === 401) { stopped = true; return; }   // โทเคนตายแล้ว — SessionGuard จัดการต่อ
    if (!res.ok) { scheduleReconnect(); return; }
    const { ticket } = await res.json();
    if (!ticket) { scheduleReconnect(); return; }
    // เช็คซ้ำ — ผู้ใช้อาจกด logout ระหว่างที่เรารอตั๋วอยู่ ถ้าไม่เช็คจะเปิดสายด้วยตั๋วของ
    // session ที่เพิ่งถูกเพิกถอนไปแล้ว
    if (stopped) return;

    closeSocket();
    // ตั๋วเดินทางใน subprotocol ไม่ใช่ query string — จะได้ไม่ไปโผล่ใน access log ของ morgan
    const sock = new WebSocket(wsUrl(), [ticket]);
    ws = sock;

    sock.onopen = () => { tries = 0; emitSystem({ topic: 'live-open' }); };   // presence.js ฟัง live-open แล้วประกาศจอตัวเองใหม่

    sock.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch (e) { return; }
      if (!msg || !msg.topic) return;

      if (msg.topic === 'server-closing') {
        // 'disabled' = สวิตช์ถูกกด ห้ามต่อใหม่ (ข้อ 4) · 'restart'/'sigterm' = ต่อใหม่ได้ตามปกติ
        if (msg.reason === 'disabled') { stopped = true; emitSystem({ topic: 'live-disabled' }); }
        return;
      }
      // presence/soft lock ไม่ใช่ "หัวข้อข้อมูลเปลี่ยน" — ไม่มีจอไหน subscribe มัน ต้องเข้าทาง
      // onSystem เหมือน session-* ไม่งั้นข้อความถูกทิ้งเงียบ ๆ (เจอจริงตอนกดผ่านจอ)
      if (msg.topic === 'presence') { emitSystem(msg); return; }
      if (msg.topic === 'session-expiring' || msg.topic === 'session-ended') {
        emitSystem(msg);
        if (msg.topic === 'session-ended') stopped = true;   // ต้องล็อกอินใหม่ก่อนถึงจะต่อได้
        return;
      }
      emit(msg.topic, msg.path);
    };

    sock.onclose = () => { if (ws === sock) ws = null; scheduleReconnect(); };
    sock.onerror = () => { /* onclose ตามมาเสมอ — ปล่อยให้ที่นั่นจัดการ */ };
  } catch (e) {
    scheduleReconnect();                     // เครือข่ายล่ม/ออฟไลน์ — ไม่ใช่เหตุให้แอปพัง
  } finally {
    starting = false;
  }
}

function closeSocket() {
  if (!ws) return;
  try { ws.onclose = null; ws.close(); } catch (e) { /* ปิดไปแล้ว */ }
  ws = null;
}

/** เริ่มใช้งาน — เรียกครั้งเดียวจาก App.vue ตอนล็อกอินสำเร็จ */
function start(tokenGetter) {
  getToken = tokenGetter;
  stopped = false;
  tries = 0;
  connect();
}

/** หยุดถาวร — logout / session จบ */
function stop() {
  stopped = true;
  if (timer) { clearTimeout(timer); timer = null; }
  closeSocket();
}

/**
 * กลับมาโฟกัส/ออนไลน์ — **ต่อใหม่และให้จอดึงข้อมูลใหม่เสมอ ห้ามเชื่อ `readyState`** (ข้อ 2)
 *
 * การ "ดึงข้อมูลใหม่" ต้องเกิดขึ้นจริง ไม่ใช่แค่คืนค่า true แล้วไม่มีใครใช้ — มือถือโค้ชที่หลับไป
 * 20 นาทีต้องเห็นตารางล่าสุดทันทีที่ปลุก ไม่ใช่รอโพล 60 วิ (ซึ่ง iOS หน่วง/หยุดตอนอยู่เบื้องหลัง)
 */
function resume() {
  if (stopped) return false;
  closeSocket();                             // ถึงจะยังดูเหมือน OPEN ก็ทิ้งแล้วต่อใหม่
  if (timer) { clearTimeout(timer); timer = null; }
  tries = 0;
  connect();
  // ปลุกผู้ฟังทุกหัวข้อให้ดึงข้อมูลของตัวเองหนึ่งรอบ (ผ่าน debounce เดียวกัน จึงไม่ยิงซ้อน)
  for (const topic of subs.keys()) emit(topic, '__resume__');
  return true;
}

/** จอสมัครรับหัวข้อ — คืนฟังก์ชันสำหรับยกเลิก (เรียกตอน unmounted เสมอ) */
function subscribe(topics, fn) {
  const list = Array.isArray(topics) ? topics : [topics];
  for (const t of list) {
    if (!subs.has(t)) subs.set(t, new Set());
    subs.get(t).add(fn);
  }
  return () => { for (const t of list) { const s = subs.get(t); if (s) s.delete(fn); } };
}

/** App.vue สมัครรับเรื่อง session (ใกล้หมดอายุ / ถูกตัด / สวิตช์ปิด) */
function onSystem(fn) {
  sysSubs.add(fn);
  return () => sysSubs.delete(fn);
}

function isConnected() { return !!ws && ws.readyState === WebSocket.OPEN; }

// ── ช่องทางขากลับ — ใช้ได้กับ presence/soft lock เท่านั้น (§3 ข้อ 1-2) ──────────
//
// ⚠️ **ห้ามส่งอะไรที่เป็นการ "สั่งให้ระบบทำอะไร" ผ่านทางนี้เด็ดขาด** — สาย WS ไม่ผ่าน
// middleware สิทธิ์ 6 ชั้นของ Express เลยสักชั้น (§11 ข้อ 1) · ทุกการเขียนข้อมูลต้องไปทาง
// HTTP ต่อไป สิ่งเดียวที่ส่งทางนี้ได้คือ "ฉันอยู่ตรงไหน" ซึ่งเป็นการบอกสถานะของตัวเอง
//
// ส่งไม่ได้ = ไม่เป็นไร ปล่อยเงียบ ๆ · presence เป็นของตกแต่ง ไม่ใช่ของที่งานต้องพึ่ง
// (สายไม่ติด = ไม่มีป้ายบอก แต่ทุกอย่างยังทำงานได้ตามปกติ)
function say(obj) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return false;
  try { ws.send(JSON.stringify(obj)); return true; } catch (e) { return false; }
}

export default { start, stop, resume, subscribe, onSystem, isConnected, say };
